import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type TeacherPortalLanguage = 'en' | 'zh';

const TEACHER_PORTAL_LANGUAGE_KEY = 'teacher_portal_language';

type TeacherPortalI18nContextValue = {
  language: TeacherPortalLanguage;
  setLanguage: (language: TeacherPortalLanguage) => void;
  t: <T>(enValue: T, zhValue: T) => T;
  apiMessage: (serverMessage: unknown, fallbackEn: string, fallbackZh: string) => string;
};

const CHINESE_CHARACTER_PATTERN = /[\u3400-\u9fff]/;

const TEACHER_PORTAL_API_MESSAGE_EN: Record<string, string> = {
  '账号或密码不能为空': 'Account and password are required.',
  '账号或密码错误': 'Incorrect account or password.',
  '请先登录老师工作台': 'Please sign in to the teacher portal first.',
  '老师账号不存在或已停用': 'The teacher account does not exist or has been disabled.',
  '教师不存在或已删除': 'The teacher does not exist or has been removed.',
  '学生不存在或已删除': 'The student does not exist or has been removed.',
  '当前老师名下暂无该学生课程': 'No lessons for this student are assigned to this teacher.',
  '无权访问该课程': 'You do not have access to this lesson.',
  '课程不存在或已删除': 'The lesson does not exist or has been removed.',
  '请选择正确的学生等级': 'Please select a valid student level.',
  '出勤状态不能为空': 'Please select the student\'s attendance status before submitting.',
  '反馈内容格式错误': 'The feedback format is invalid.',
  '请先选择教材': 'Please select a textbook first.',
  '该教材不在老师可授范围内': 'This textbook is not within your supported teaching materials.',
  '仅首次约您的课程需要确认教材进度': 'Textbook progress only needs confirmation for the student\'s first lesson with you.',
  '仅待上课课程支持确认教材进度': 'Textbook progress can only be confirmed for upcoming lessons.',
  '请选择下节课学习内容': 'Please select the next lesson content.',
  '请选择要调整的教材目录': 'Please select the textbook chapter to update.',
  '请先选择要调整的课程': 'Please select the lesson to update first.',
  '仅待开始课程支持修改教材进度': 'Textbook progress can only be updated for upcoming lessons.',
  '课程已开始或已结束，暂不支持修改教材进度': 'Textbook progress cannot be updated after the lesson has started.',
  '当前课程状态不支持取消': 'This lesson cannot be cancelled in its current status.',
  '距离上课不足16小时，请联系助教处理': 'This lesson starts in less than 16 hours. Please contact the coordinator.',
  '课程时间信息无效，无法取消': 'The lesson time is invalid and it cannot be cancelled.',
  '课程学点数无效': 'The lesson credit amount is invalid.',
  '排课不存在或已删除，请刷新后重试': 'This availability slot no longer exists. Please refresh and try again.',
  '无权限删除该老师的排课': 'You do not have permission to remove this teacher\'s availability.',
  '已被预约的时间不能删除': 'A booked time slot cannot be removed.',
  '删除排课失败，请刷新后重试': 'Failed to remove availability. Please refresh and try again.',
  '排课结束时间必须晚于开始时间': 'The end time must be later than the start time.',
  '只能排当前时间之后最近的半小时时段': 'Availability must start on a future half-hour time slot.',
  '排课时间格式不正确': 'The availability time format is invalid.',
  '请求超时，请检查网络后重试': 'The request timed out. Please check your network and try again.',
  '网络错误，请检查网络连接': 'Network error. Please check your connection and try again.',
  '请求失败': 'Request failed. Please try again.',
  '本节课学习内容不能为空': 'This lesson content is required.',
  '下节课学习内容不能为空': 'Next lesson content is required.',
  '教材进度范围不能为空': 'The textbook progress range is required.',
  '本节课学习内容不在当前教材可选范围内': 'This lesson content is outside the available range for the current textbook.',
  '下节课学习内容不在当前教材可选范围内': 'Next lesson content is outside the available range for the current textbook.',
  '教材进度范围不在当前教材可选范围内': 'The textbook progress range is outside the available range for the current textbook.',
  '本节课学习内容的结束章节不能早于开始章节': 'The end chapter for this lesson cannot be earlier than the start chapter.',
  '本节课实际学习范围最多跨到相邻的下一个Level，请重新选择': 'This lesson may cross only into the immediately following level. Please choose the range again.',
  '本节课实际学习范围最多覆盖3个连续最小目录，请重新选择': 'One lesson may cover at most 3 continuous smallest directories. Please choose the range again.',
  '本节课实际学习范围最多覆盖4个连续最小目录，请重新选择': 'One lesson may cover at most 4 continuous smallest directories. Please choose the range again.',
  '本节课实际开始章节与预约进度差异较大，请确认调整原因后再提交': 'The actual lesson start differs substantially from the booked progress. Confirm the adjustment reason before submitting.',
  '下节课学习内容最多跨到相邻的下一个Level，请重新选择': 'The next lesson may cross only into the immediately following level. Please choose the range again.',
  '下节课学习内容最多覆盖3个连续最小目录，请重新选择': 'The next lesson may cover at most 3 continuous smallest directories. Please choose the range again.',
  '下节课学习内容的结束章节不能早于开始章节': 'The end chapter for the next lesson cannot be earlier than the start chapter.',
  '教材进度范围的结束章节不能早于开始章节': 'The end chapter of the textbook progress range cannot be earlier than the start chapter.',
  '本节课学习内容必须在同一教材内': 'The start and end chapters for this lesson must be in the same textbook.',
  '下节课学习内容必须在同一教材内': 'The start and end chapters for the next lesson must be in the same textbook.',
  '教材进度范围必须在同一教材内': 'The start and end chapters of the textbook progress range must be in the same textbook.',
  '本节课学习内容不在老师可授教材范围内': 'This lesson content is not within your supported teaching materials.',
  '下节课学习内容不在老师可授教材范围内': 'Next lesson content is not within your supported teaching materials.',
  '教材进度范围不在老师可授教材范围内': 'The textbook progress range is not within your supported teaching materials.',
};

const TEACHER_PORTAL_API_MESSAGE_EN_PATTERNS: Array<{
  pattern: RegExp;
  message: string;
}> = [
  {
    pattern: /本节课开始页.*不属于所选教材/,
    message: 'This lesson\'s start page does not belong to the selected textbook. Reopen the start-page picker and choose a page from the selected chapter.',
  },
  {
    pattern: /本节课结束页.*不属于所选教材/,
    message: 'This lesson\'s end page does not belong to the selected textbook. Reopen the end-page picker and choose a page from the selected chapter.',
  },
  {
    pattern: /下节课开始页.*不属于所选教材/,
    message: 'The next lesson\'s start page does not belong to the selected textbook. Reopen the next-start picker and choose a page from the selected chapter.',
  },
  {
    pattern: /下节课结束页.*不属于所选教材/,
    message: 'The next lesson\'s end page does not belong to the selected textbook. Reopen the estimated-end picker and choose a page from the selected chapter.',
  },
  {
    pattern: /本节课结束页与所选章节不一致/,
    message: 'This lesson\'s end page does not match its chapter. Reopen the end-page picker and select the correct page before submitting.',
  },
  {
    pattern: /下节课开始页与所选章节不一致/,
    message: 'The next lesson\'s start page does not match its chapter. Reopen the next-start picker and select the correct page before submitting.',
  },
  {
    pattern: /(?:所选课件页|本节课实际页码|下节课计划页码).*教材章节不一致/,
    message: 'A selected courseware page does not match its chapter. Refresh this page to sync the latest courseware position, then reselect the highlighted start or end point.',
  },
  {
    pattern: /(?:本节课实际页码|下节课计划页码).*结束页不能早于开始页/,
    message: 'The end page cannot be earlier than the start page. Please correct the highlighted page range before submitting.',
  },
  {
    pattern: /本节课开始页与上一节课已学习范围重复/,
    message: 'This lesson moves back into earlier content. If the student needs to revisit it, select a reason and confirm the backward adjustment; otherwise set the start near the previous lesson end.',
  },
  {
    pattern: /请在本节课结束后确认学生缺课/,
    message: 'Please wait until this lesson ends before confirming student absence.',
  },
  {
    pattern: /已取消的课程不能提交缺课反馈/,
    message: 'This lesson was canceled. Student absence cannot be submitted for a canceled lesson.',
  },
  {
    pattern: /本节课已确认学生缺课，不能推进绘本/,
    message: 'This lesson was marked absent. Open the next lesson to continue reading with the student.',
  },
  {
    pattern: /下节课开始位置早于本节课结束位置/,
    message: 'The next lesson starts before this lesson ends and would repeat content. Set the next start to this lesson\'s end page or the immediately following page.',
  },
  {
    pattern: /所选教材目录暂无可用课件页面/,
    message: 'The selected chapter has no available courseware pages. Choose another chapter or contact the coordinator.',
  },
  {
    pattern: /(?:本节课实际开始章节|下节课学习内容).*请确认.*再提交/,
    message: 'This is an unusual progress adjustment. Review the comparison, select a reason, confirm the adjustment, and then submit again.',
  },
];

/**
 * Teacher APIs historically return Chinese business messages. Keep their detail in
 * Chinese mode, but never leak those messages into the English teacher workspace.
 */
export const localizeTeacherPortalApiMessage = (
  serverMessage: unknown,
  language: TeacherPortalLanguage,
  fallbackEn: string,
  fallbackZh: string
): string => {
  const normalizedMessage = typeof serverMessage === 'string' ? serverMessage.trim() : '';
  if (!normalizedMessage) {
    return language === 'zh' ? fallbackZh : fallbackEn;
  }
  if (language === 'zh') {
    return normalizedMessage;
  }
  if (!CHINESE_CHARACTER_PATTERN.test(normalizedMessage)) {
    return normalizedMessage;
  }
  const patternedMessage = TEACHER_PORTAL_API_MESSAGE_EN_PATTERNS.find(({ pattern }) =>
    pattern.test(normalizedMessage)
  );
  return TEACHER_PORTAL_API_MESSAGE_EN[normalizedMessage] || patternedMessage?.message || fallbackEn;
};

const TeacherPortalI18nContext = createContext<TeacherPortalI18nContextValue | null>(null);

const getInitialLanguage = (): TeacherPortalLanguage => {
  const stored = localStorage.getItem(TEACHER_PORTAL_LANGUAGE_KEY);
  if (stored === 'zh' || stored === 'en') {
    return stored;
  }
  return 'en';
};

export const TeacherPortalI18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<TeacherPortalLanguage>(getInitialLanguage);

  useEffect(() => {
    localStorage.setItem(TEACHER_PORTAL_LANGUAGE_KEY, language);
  }, [language]);

  const value = useMemo<TeacherPortalI18nContextValue>(
    () => ({
      language,
      setLanguage: setLanguageState,
      t: (enValue, zhValue) => (language === 'zh' ? zhValue : enValue),
      apiMessage: (serverMessage, fallbackEn, fallbackZh) =>
        localizeTeacherPortalApiMessage(serverMessage, language, fallbackEn, fallbackZh),
    }),
    [language]
  );

  return (
    <TeacherPortalI18nContext.Provider value={value}>
      {children}
    </TeacherPortalI18nContext.Provider>
  );
};

export const useTeacherPortalI18n = (): TeacherPortalI18nContextValue => {
  const context = useContext(TeacherPortalI18nContext);
  if (!context) {
    throw new Error('useTeacherPortalI18n must be used within TeacherPortalI18nProvider');
  }
  return context;
};
